import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styles from '../GenerationWorkspace.module.css';
import { Tooltip } from '../../common/Tooltip';
import { useInfiniteGenerationJobs, type GenerationJob } from '../../../hooks/useGenerationJobs';
import { jobHistoryExpandedKey } from '../../../utils/jobHistoryStorage';
import { brandConfig } from '../../../config/brands';
import { useGenerationModels } from '../../../features/generation-models/hooks';
import { formatVideoResolutionLabel } from '../state/useGenerationState';
import { apiClient } from '../../../services/apiClient';
import { GenerationInfoModal } from '../../materials/shared/GenerationInfoModal';
import { hasGenerationInfo } from '../../materials/shared/generationInfoUtils';
import type { TemporaryMaterial } from '../../../features/materials/materialsSlice';

async function fetchTemporaryMaterial(materialId: string): Promise<TemporaryMaterial> {
  const { data } = await apiClient.get<TemporaryMaterial>(`/materials/temporary/${materialId}`);
  return data;
}

const HISTORY_KEY = jobHistoryExpandedKey('video');

export type VideoJobRestorePayload = {
  prompt_text?: string;
  video_model?: string;
  resolution?: string | number;
  aspect_ratio?: string;
  duration_seconds?: number;
  generate_audio?: boolean;
  resize_mode?: string;
  negative_prompt?: string;
  /** generation_metadata から取得した、復元すべき素材ID群（prompt モードのみ）
   * キー自体が generation_metadata に存在しない旧データでは undefined。
   * 存在する場合は配列／null で明示される。 */
  first_frame_material_id?: string | null;
  last_frame_material_id?: string | null;
  reference_material_ids?: string[] | null;
  /** video-to-video の参照動画素材ID群（prompt モードのみ） */
  reference_video_material_ids?: string[] | null;
  /** 復元対象に素材情報が含まれているか（= 新メタデータで素材を復元すべきか） */
  has_asset_info?: boolean;
};

type Props = {
  onPreview: (materialId: string) => void;
  onRestore: (payload: VideoJobRestorePayload) => void;
};

function formatCreatedAt(iso: string): string {
  const date = new Date(iso);
  const yyyy = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();
  const hh = `${date.getHours()}`.padStart(2, '0');
  const mm = `${date.getMinutes()}`.padStart(2, '0');
  const ss = `${date.getSeconds()}`.padStart(2, '0');
  return `${yyyy}/${m}/${d} ${hh}:${mm}:${ss}`;
}

export function VideoJobHistoryPanel({ onPreview, onRestore }: Props) {
  const queryClient = useQueryClient();
  const [infoModalMaterialId, setInfoModalMaterialId] = useState<string | null>(null);
  const [expanded, setExpandedState] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(HISTORY_KEY) : null;
    if (saved !== null) return saved === '1';
    return brandConfig.id === 'hypeapp';
  });
  const setExpanded = useCallback((next: boolean) => {
    setExpandedState(next);
    try {
      localStorage.setItem(HISTORY_KEY, next ? '1' : '0');
    } catch {
      // ignore
    }
  }, []);

  const jobsQuery = useInfiniteGenerationJobs(20, 'video');
  const jobs = useMemo<GenerationJob[]>(() => {
    return jobsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  }, [jobsQuery.data]);
  const hasNextPage = jobsQuery.hasNextPage;

  const { data: videoModels = [] } = useGenerationModels('video');
  const resolveModelLabel = useCallback((code?: string) => {
    if (!code) return undefined;
    const exact = videoModels.find((m) => m.model_code === code);
    if (exact) return exact.display_label;
    const prefix = videoModels
      .filter((m) => code.startsWith(m.model_code))
      .sort((a, b) => b.model_code.length - a.model_code.length)[0];
    return prefix ? prefix.display_label : code;
  }, [videoModels]);

  return (
    <section className={styles.section} style={{ marginTop: '16px' }}>
      {jobsQuery.isError ? (
        <div className={styles.error}>生成履歴の取得に失敗しました</div>
      ) : (
        <div className={styles.jobHistory}>
          <div className={styles.historyCollapse}>
            <button
              type="button"
              className={`${styles.historyToggle} ${expanded ? styles.historyToggleExpandedTop : ''}`}
              onClick={() => setExpanded(!expanded)}
              disabled={jobsQuery.isLoading}
            >
              <img src="/time_clock_circle.svg" alt="生成履歴" className={styles.historyIcon} />
              <span className={styles.historyLabel}>
                {jobsQuery.isLoading ? '生成履歴を取得中…' : '生成履歴'}
              </span>
            </button>
            <div
              className={`${styles.historyPanelContent} ${expanded ? styles.historyPanelContentOpen : ''}`}
              aria-hidden={!expanded}
            >
              <div className={styles.historyPanelContentInner}>
                {jobs.length > 0 ? (
                  <>
                    <div className={styles.historyHeader}>
                      {jobsQuery.isFetching && <div className="spinner spinner-sm" />}
                      <button
                        className={styles.textLink}
                        type="button"
                        onClick={() => jobsQuery.refetch()}
                        disabled={jobsQuery.isFetching}
                      >
                        更新
                      </button>
                    </div>
                    <ul className={styles.jobList}>
                      {jobs.map((job) => (
                        <VideoJobListItem
                          key={job.id}
                          job={job}
                          onPreview={onPreview}
                          onRestore={onRestore}
                          onShowInfo={(mid) => setInfoModalMaterialId(mid)}
                          modelLabel={resolveModelLabel(job.video_model) ?? resolveModelLabel(job.model)}
                        />
                      ))}
                    </ul>
                    {hasNextPage && (
                      <button
                        type="button"
                        className={styles.loadMoreButton}
                        onClick={() => jobsQuery.fetchNextPage()}
                        disabled={jobsQuery.isFetchingNextPage}
                      >
                        {jobsQuery.isFetchingNextPage ? '読み込み中…' : 'もっと見る ▾'}
                      </button>
                    )}
                    <button
                      type="button"
                      className={`${styles.historyToggle} ${styles.historyToggleExpanded}`}
                      onClick={() => setExpanded(false)}
                    >
                      <img src="/unfold_less_fill.svg" alt="" className={styles.historyIcon} />
                      <span className={styles.historyLabel}>折りたたむ</span>
                    </button>
                  </>
                ) : (
                  <div className={styles.empty} style={{ padding: '12px 0' }}>
                    まだ動画生成履歴はありません
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {infoModalMaterialId && (() => {
        const cached = queryClient.getQueryData<TemporaryMaterial>([
          'temporary-material',
          infoModalMaterialId,
        ]);
        if (!cached) return null;
        return (
          <GenerationInfoModal
            material={cached}
            fullSizeImageUrl={null}
            onClose={() => setInfoModalMaterialId(null)}
          />
        );
      })()}
    </section>
  );
}

function VideoJobListItem({
  job,
  onPreview,
  onRestore,
  onShowInfo,
  modelLabel,
}: {
  job: GenerationJob;
  onPreview: (materialId: string) => void;
  onRestore: (payload: VideoJobRestorePayload) => void;
  onShowInfo: (materialId: string) => void;
  modelLabel?: string;
}) {
  const queryClient = useQueryClient();
  const createdAt = useMemo(() => formatCreatedAt(job.created_at), [job.created_at]);
  const isDeleted = job.is_deleted || !job.material_id;
  const isFailed = job.status === 'failed';
  const statusText = job.status === 'completed' ? '完了' : job.status === 'failed' ? '失敗' : job.status;
  const userInputs = job.user_inputs;
  const isScenarioJob =
    Boolean(job.scene_id || job.scenario_id) || userInputs?.mode === 'scenario';
  // プロンプトモードだけユーザー入力を表示。scenario/unknown は非表示。
  const displayablePrompt = userInputs?.mode === 'prompt' ? (userInputs.prompt ?? '').trim() : '';
  // 追加プロンプト系はモードを問わず表示する (静止画の生成履歴と同じく、ユーザー起源
  // 入力だけを保持する user_inputs から読む。内部テンプレは混ざらない)。
  const scenarioAdditionalPrompt = (userInputs?.scenario_additional_prompt ?? '').trim();
  const sceneAdditionalPrompt = (userInputs?.additional_prompt ?? '').trim();
  const hasAnyPromptText = Boolean(displayablePrompt || scenarioAdditionalPrompt || sceneAdditionalPrompt);

  // AI生成情報の有無判定のため material を取得（完了済かつ未削除のみ）
  const materialQuery = useQuery({
    queryKey: ['temporary-material', job.material_id],
    queryFn: async () => {
      const cached = queryClient.getQueryData<{ items: TemporaryMaterial[] }>(['materials', 'temporary']);
      const found = cached?.items?.find((m) => m.id === job.material_id);
      if (found) return found;
      return fetchTemporaryMaterial(job.material_id!);
    },
    enabled: !!job.material_id && !isDeleted && job.status === 'completed',
    staleTime: 60_000,
  });
  const material = materialQuery.data as Record<string, unknown> | undefined;
  const genMeta = material?.generation_metadata as Record<string, unknown> | undefined;
  const hasInfo = hasGenerationInfo(genMeta);

  const badges = useMemo<{ label: string; value: string }[]>(() => {
    const items: { label: string; value: string }[] = [];
    if (job.aspect_ratio) items.push({ label: 'アスペクト比', value: String(job.aspect_ratio) });
    if (job.resolution) items.push({ label: '解像度', value: formatVideoResolutionLabel(String(job.resolution)) });
    if (job.duration_seconds != null) items.push({ label: '尺', value: `${job.duration_seconds}秒` });
    if (typeof job.generate_audio === 'boolean') {
      items.push({ label: '音声', value: job.generate_audio ? '音声あり' : '音声なし' });
    }
    if (modelLabel) items.push({ label: 'モデル', value: modelLabel });
    return items;
  }, [job.aspect_ratio, job.resolution, job.duration_seconds, job.generate_audio, modelLabel]);

  const handleRestore = () => {
    // 素材復元は generation_metadata に material_id が書かれている新データのみ可能。
    // 旧データはキーがそもそも存在しないので undefined のまま渡し、ハンドラで「触らない」判定に使う。
    const hasAssetInfo = !!genMeta && (
      'first_frame_material_id' in genMeta ||
      'last_frame_material_id' in genMeta ||
      'reference_material_ids' in genMeta ||
      'reference_video_material_ids' in genMeta
    );
    const firstMid = genMeta?.first_frame_material_id;
    const lastMid = genMeta?.last_frame_material_id;
    const refMidsRaw = genMeta?.reference_material_ids;
    const refMids = Array.isArray(refMidsRaw)
      ? refMidsRaw.filter((v): v is string => typeof v === 'string' && v.length > 0)
      : (refMidsRaw === null ? null : undefined);
    const refVidMidsRaw = genMeta?.reference_video_material_ids;
    const refVidMids = Array.isArray(refVidMidsRaw)
      ? refVidMidsRaw.filter((v): v is string => typeof v === 'string' && v.length > 0)
      : (refVidMidsRaw === null ? null : undefined);

    onRestore({
      prompt_text: displayablePrompt,
      video_model: job.video_model,
      resolution: job.resolution,
      aspect_ratio: job.aspect_ratio,
      duration_seconds: job.duration_seconds,
      generate_audio: job.generate_audio,
      resize_mode: job.resize_mode,
      negative_prompt: userInputs?.negative_prompt,
      first_frame_material_id: typeof firstMid === 'string' && firstMid ? firstMid : (firstMid === null ? null : undefined),
      last_frame_material_id: typeof lastMid === 'string' && lastMid ? lastMid : (lastMid === null ? null : undefined),
      reference_material_ids: refMids,
      reference_video_material_ids: refVidMids,
      has_asset_info: hasAssetInfo,
    });
  };

  return (
    <li className={styles.jobItem}>
      {isFailed ? (
        <div className={styles.jobThumbnailPlaceholder} title={job.error_message || 'エラーが発生しました'}>
          <span className={styles.deletedText}>エラー</span>
        </div>
      ) : job.thumbnail_url && !isDeleted ? (
        <button
          type="button"
          className={styles.jobThumbnailButton}
          onClick={() => job.material_id && onPreview(job.material_id)}
          aria-label={`動画ジョブ ${job.id} を再生`}
        >
          <img src={job.thumbnail_url} alt="動画サムネイル" />
        </button>
      ) : (
        <div className={styles.jobThumbnailPlaceholder}>
          <span className={styles.deletedText}>削除されました</span>
        </div>
      )}
      <div className={styles.jobBody}>
        {!isDeleted && (
          <>
            {(displayablePrompt || !hasAnyPromptText) && (
              <p className={styles.jobPrompt} title={displayablePrompt}>
                {displayablePrompt ? (
                  <><span className={styles.jobTextLabel}>プロンプト</span>{displayablePrompt}</>
                ) : (
                  isFailed ? '' : '（プロンプト情報なし）'
                )}
              </p>
            )}
            {scenarioAdditionalPrompt && (
              <p className={styles.jobPrompt} title={scenarioAdditionalPrompt}>
                <span className={styles.jobTextLabel}>シナリオ生成の追加プロンプト</span>{scenarioAdditionalPrompt}
              </p>
            )}
            {sceneAdditionalPrompt && (
              <p className={styles.jobPrompt} title={sceneAdditionalPrompt}>
                <span className={styles.jobTextLabel}>動画生成の追加プロンプト</span>{sceneAdditionalPrompt}
              </p>
            )}
            <div className={styles.jobReqs}>
              {isScenarioJob && (
                <Tooltip content="シナリオ経由で生成された動画" placement="bottom">
                  <span className={styles.jobReqBadge}>シナリオ</span>
                </Tooltip>
              )}
              {badges.map((req) => (
                <Tooltip key={`${req.label}-${req.value}`} content={req.label} placement="bottom">
                  <span className={styles.jobReqBadge}>{req.value}</span>
                </Tooltip>
              ))}
            </div>
          </>
        )}
        <div className={styles.jobBodyFooter}>
          <span className={styles.jobTimestamp}>{createdAt}</span>
          {!isDeleted && hasInfo && job.material_id && (
            <button
              type="button"
              className={styles.jobInfoLink}
              onClick={() => onShowInfo(job.material_id!)}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4" />
                <path d="M12 8h.01" />
              </svg>
              AI生成情報
            </button>
          )}
          {!isDeleted && (
            <Tooltip
              content={isScenarioJob ? 'シナリオ動画は復元できません' : 'この設定を復元'}
              placement="bottom"
              className={styles.jobRestoreTooltip}
            >
              <button
                type="button"
                className={styles.jobRestoreButton}
                onClick={handleRestore}
                disabled={isScenarioJob || (!displayablePrompt && !job.video_model)}
              >
                <img src="/navigation_direction_left_forward.svg" alt="" width="14" height="14" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      <span className={`${job.status === 'completed' ? styles.badge : job.status === 'failed' ? styles.badgeError : styles.badgePending} ${styles.jobStatus}`}>
        {statusText}
      </span>
    </li>
  );
}
